
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Plus, Search, Pencil, Trash, HeartPulse } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { calculateBMI } from "@/lib/fitness-calculations";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { useAuth } from "@/components/AuthProvider";

export const AssessmentsList = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();
  const { t } = useTranslations("assessments");
  const [searchTerm, setSearchTerm] = useState("");
  const [assessmentToDelete, setAssessmentToDelete] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: assessments, isLoading } = useQuery({
    queryKey: ["physical-assessments"],
    queryFn: async () => {
      if (!user?.id) {
        console.error("No authenticated user found");
        return [];
      }
      
      const { data, error } = await supabase
        .from("physical_assessments")
        .select(`
          *,
          students (
            first_name,
            last_name,
            height
          )
        `)
        .eq("trainer_id", user.id) // Ensure we only get assessments for this trainer
        .order("assessment_date", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id
  });

  const handleDelete = async (id: string) => {
    try {
      if (!user?.id) {
        throw new Error("No authenticated user found");
      }
      
      // First verify this assessment belongs to the current trainer
      const { data: assessmentData, error: verifyError } = await supabase
        .from("physical_assessments")
        .select("id")
        .eq("id", id)
        .eq("trainer_id", user.id)
        .single();
        
      if (verifyError || !assessmentData) {
        throw new Error("Assessment not found or you don't have permission to delete");
      }
      
      const { error } = await supabase
        .from("physical_assessments")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success(t("notifications.deleted"));
      setAssessmentToDelete(null);
      queryClient.invalidateQueries({ queryKey: ["physical-assessments"] });
    } catch (error: any) {
      console.error("Error deleting assessment:", error);
      toast.error(error.message || "Error deleting assessment");
    }
  };

  const filteredAssessments = assessments?.filter((assessment) => {
    const searchString = searchTerm.toLowerCase();
    return (
      assessment.students.first_name.toLowerCase().includes(searchString) ||
      assessment.students.last_name.toLowerCase().includes(searchString)
    );
  });

  const dateLocale = language === 'pt' ? ptBR : enUS;

  return (
    <div className="space-y-4 w-full max-w-full overflow-x-hidden animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100/50 dark:border-gray-700">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold tracking-tight gradient-text dark:text-white">{t("title")}</h2>
          <p className="text-muted-foreground dark:text-gray-300">
            {t("subtitle")}
          </p>
          <Breadcrumb className="flex-wrap">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/dashboard")} className="text-flame hover:text-flame/80 dark:text-flame dark:hover:text-flame/80">Dashboard</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-medium dark:text-white">{t("title")}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <Button onClick={() => navigate(getPath("/dashboard/assessments/new"))} className="bg-flame hover:bg-flame/90">
          <Plus className="mr-2 h-4 w-4" />
          {t("list.new")}
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground dark:text-gray-400" />
          <Input
            placeholder={t("list.search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 bg-white/90 border border-slate-100 rounded-xl shadow-sm focus:ring-1 focus:ring-flame/20 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder:text-gray-400"
          />
        </div>
      </div>

      <div className="bg-white/90 backdrop-blur-lg rounded-xl shadow-md border border-slate-100/50 overflow-hidden hover:shadow-lg transition-all dark:bg-gray-800 dark:border-gray-700">
        <div className="min-w-full inline-block align-middle">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50 dark:bg-gray-900/50">
                  <TableHead className="font-medium dark:text-gray-200">{t("list.columns.student")}</TableHead>
                  <TableHead className="font-medium dark:text-gray-200">{t("list.columns.date")}</TableHead>
                  <TableHead className="font-medium dark:text-gray-200">{t("list.columns.weight")}</TableHead>
                  <TableHead className="font-medium dark:text-gray-200">{t("list.columns.bodyFat")}</TableHead>
                  <TableHead className="font-medium dark:text-gray-200">{t("list.columns.bmi")}</TableHead>
                  <TableHead className="text-right font-medium dark:text-gray-200">{t("list.columns.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center dark:text-gray-300">
                      <div className="py-8 flex flex-col items-center justify-center">
                        <HeartPulse className="h-10 w-10 text-flame/40 mb-2 animate-pulse" />
                        <span className="text-muted-foreground dark:text-gray-400">{t("list.loading")}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredAssessments?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center dark:text-gray-300">
                      <div className="py-8 flex flex-col items-center justify-center">
                        <HeartPulse className="h-10 w-10 text-flame/40 mb-2" />
                        <span className="text-muted-foreground dark:text-gray-400">{t("list.noAssessments")}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAssessments?.map((assessment) => (
                    <TableRow key={assessment.id} className="hover:bg-slate-50/50 dark:hover:bg-gray-700/50">
                      <TableCell className="dark:text-gray-300">
                        {assessment.students.first_name} {assessment.students.last_name}
                      </TableCell>
                      <TableCell className="dark:text-gray-300">
                        {format(new Date(assessment.assessment_date), "dd/MM/yyyy", {
                          locale: dateLocale,
                        })}
                      </TableCell>
                      <TableCell className="dark:text-gray-300">{assessment.weight}</TableCell>
                      <TableCell className="dark:text-gray-300">{assessment.body_fat_percentage}%</TableCell>
                      <TableCell className="dark:text-gray-300">
                        {assessment.weight && assessment.students.height
                          ? calculateBMI(assessment.weight, assessment.students.height).toFixed(1)
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(getPath(`/dashboard/assessments/edit/${assessment.id}`))}
                          className="hover:text-flame/80 dark:text-gray-300 dark:hover:text-flame/80"
                        >
                          <Pencil className="h-4 w-4" />
                          <span className="sr-only">Edit</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAssessmentToDelete(assessment.id)}
                          className="hover:text-red-500 dark:text-gray-300 dark:hover:text-red-500"
                        >
                          <Trash className="h-4 w-4" />
                          <span className="sr-only">Delete</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <AlertDialog open={!!assessmentToDelete} onOpenChange={() => setAssessmentToDelete(null)}>
        <AlertDialogContent className="dark:bg-gray-800 dark:border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-white">{t("deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-gray-300">
              {t("deleteDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600">{t("deleteDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => assessmentToDelete && handleDelete(assessmentToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("deleteDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
